import {
  BotState,
  ChoiceState,
  CollectState,
  ConditionState,
} from '@/services/bot-flows.service';

export interface FlowValidationIssue {
  stateId: string | null;
  level: 'error' | 'warning';
  message: string;
}

export interface FlowValidationResult {
  errors: FlowValidationIssue[];
  warnings: FlowValidationIssue[];
  errorsByStateId: Map<string, string[]>;
  warningsByStateId: Map<string, string[]>;
}

export function validateFlowClient(
  states: BotState[],
  startStateId: string,
): FlowValidationResult {
  const errors: FlowValidationIssue[] = [];
  const warnings: FlowValidationIssue[] = [];

  const stateIds = new Set(states.map((s) => s.id));

  // Duplicate state IDs
  const seen = new Set<string>();
  for (const s of states) {
    if (seen.has(s.id)) {
      errors.push({ stateId: s.id, level: 'error', message: `Duplicate state ID: "${s.id}"` });
    }
    seen.add(s.id);
  }

  // startStateId exists
  if (!startStateId) {
    errors.push({ stateId: null, level: 'error', message: 'startStateId is not set' });
  } else if (!stateIds.has(startStateId)) {
    errors.push({ stateId: null, level: 'error', message: `startStateId "${startStateId}" does not exist` });
  }

  // Per-state validation
  for (const state of states) {
    switch (state.type) {
      case 'message': {
        if (state.next && !stateIds.has(state.next)) {
          errors.push({
            stateId: state.id,
            level: 'error',
            message: `next "${state.next}" is not a valid state`,
          });
        }
        if (!state.prompt && !state.templateUid) {
          warnings.push({
            stateId: state.id,
            level: 'warning',
            message: 'Message state has no prompt or template',
          });
        }
        break;
      }
      case 'choice': {
        const cs = state as ChoiceState;
        const presentation = cs.presentationType ?? 'buttons';

        if (presentation === 'media_buttons') {
          if (!cs.mediaUrl) {
            errors.push({
              stateId: state.id,
              level: 'error',
              message: 'Media + Buttons: a media URL is required',
            });
          }
          if ((cs.options?.length ?? 0) > 3) {
            errors.push({
              stateId: state.id,
              level: 'error',
              message: 'Media + Buttons: WhatsApp allows at most 3 buttons',
            });
          }
        }

        if (presentation === 'list') {
          const sections = cs.sections ?? [];
          if (sections.length === 0) {
            errors.push({
              stateId: state.id,
              level: 'error',
              message: 'List menu: at least one section is required',
            });
          }
          if (sections.length > 10) {
            errors.push({
              stateId: state.id,
              level: 'error',
              message: 'List menu: WhatsApp allows at most 10 sections',
            });
          }
          let totalRows = 0;
          sections.forEach((sec, i) => {
            const rows = sec.rows ?? [];
            if (rows.length === 0) {
              warnings.push({
                stateId: state.id,
                level: 'warning',
                message: `List menu: section ${i + 1} has no rows`,
              });
            }
            if (rows.length > 10) {
              errors.push({
                stateId: state.id,
                level: 'error',
                message: `List menu: section ${i + 1} exceeds 10 rows`,
              });
            }
            for (const row of rows) {
              if (row.nextStateId && !stateIds.has(row.nextStateId)) {
                errors.push({
                  stateId: state.id,
                  level: 'error',
                  message: `List row "${row.label}" points to non-existent state "${row.nextStateId}"`,
                });
              }
              totalRows++;
            }
          });
          if (totalRows > 10) errors.push({ stateId: state.id, level: 'error', message: 'WhatsApp allows at most 10 rows across all list sections.' });
          if (totalRows === 0 && sections.length > 0) {
            warnings.push({
              stateId: state.id,
              level: 'warning',
              message: 'List menu has no rows yet',
            });
          }
        } else {
          if (!cs.options || cs.options.length === 0) {
            warnings.push({
              stateId: state.id,
              level: 'warning',
              message: 'Choice state has no options',
            });
          }
          for (const opt of cs.options ?? []) {
            if (opt.nextStateId && !stateIds.has(opt.nextStateId)) {
              errors.push({
                stateId: state.id,
                level: 'error',
                message: `Option "${opt.label}" points to non-existent state "${opt.nextStateId}"`,
              });
            }
          }
        }
        break;
      }
      case 'collect': {
        const col = state as CollectState;
        if (!col.saveAs) {
          errors.push({
            stateId: state.id,
            level: 'error',
            message: 'Collect state is missing "saveAs"',
          });
        }
        if (!stateIds.has(col.next)) {
          errors.push({
            stateId: state.id,
            level: 'error',
            message: `next "${col.next}" is not a valid state`,
          });
        }
        break;
      }
      case 'condition': {
        const cond = state as ConditionState;
        for (const branch of cond.branches ?? []) {
          if (branch.nextStateId && !stateIds.has(branch.nextStateId)) {
            errors.push({
              stateId: state.id,
              level: 'error',
              message: `Branch nextStateId "${branch.nextStateId}" is not a valid state`,
            });
          }
        }
        if (cond.fallbackStateId && !stateIds.has(cond.fallbackStateId)) {
          errors.push({
            stateId: state.id,
            level: 'error',
            message: `fallbackStateId "${cond.fallbackStateId}" is not a valid state`,
          });
        }
        break;
      }
      case 'handoff':
      case 'end':
        break;
      case 'ai': {
        if (!state.systemPrompt.trim()) errors.push({ stateId: state.id, level: 'error', message: 'Describe the AI assistant instructions.' });
        if (!state.fallbackStateId || !stateIds.has(state.fallbackStateId)) errors.push({ stateId: state.id, level: 'error', message: 'Choose a fallback state for the AI assistant.' });
        for (const intent of state.endIntents) {
          if (!stateIds.has(state.intentToNextState[intent])) errors.push({ stateId: state.id, level: 'error', message: `Choose the next state for AI intent "${intent}".` });
        }
        break;
      }
    }
  }

  // Unreachable states via BFS
  if (startStateId && stateIds.has(startStateId)) {
    const visited = new Set<string>();
    const queue = [startStateId];
    visited.add(startStateId);

    while (queue.length > 0) {
      const current = queue.shift()!;
      const state = states.find((s) => s.id === current);
      if (!state) continue;

      const neighbors = getNeighbors(state);
      for (const n of neighbors) {
        if (n && stateIds.has(n) && !visited.has(n)) {
          visited.add(n);
          queue.push(n);
        }
      }
    }

    for (const state of states) {
      if (!visited.has(state.id)) {
        warnings.push({
          stateId: state.id,
          level: 'warning',
          message: `State "${state.id}" is unreachable from start`,
        });
      }
    }
  }

  // Warn: no end state
  const hasEnd = states.some((s) => s.type === 'end');
  if (!hasEnd && states.length > 0) {
    warnings.push({
      stateId: null,
      level: 'warning',
      message: 'Flow has no "end" state — may loop or not resolve the lead status',
    });
  }

  // Build per-state maps
  const errorsByStateId = new Map<string, string[]>();
  const warningsByStateId = new Map<string, string[]>();

  for (const e of errors) {
    if (e.stateId) {
      const arr = errorsByStateId.get(e.stateId) ?? [];
      arr.push(e.message);
      errorsByStateId.set(e.stateId, arr);
    }
  }
  for (const w of warnings) {
    if (w.stateId) {
      const arr = warningsByStateId.get(w.stateId) ?? [];
      arr.push(w.message);
      warningsByStateId.set(w.stateId, arr);
    }
  }

  return { errors, warnings, errorsByStateId, warningsByStateId };
}

function getNeighbors(state: BotState): string[] {
  switch (state.type) {
    case 'message':
      return state.next ? [state.next] : [];
    case 'choice':
      return (state.options ?? []).map((o) => o.nextStateId);
    case 'collect':
      return [state.next];
    case 'condition': {
      const neighbors = (state.branches ?? []).map((b) => b.nextStateId);
      if (state.fallbackStateId) neighbors.push(state.fallbackStateId);
      return neighbors;
    }
    case 'handoff':
    case 'end':
      return [];
    case 'ai':
      return [...Object.values(state.intentToNextState ?? {}), ...(state.fallbackStateId ? [state.fallbackStateId] : [])];
    default:
      return [];
  }
}
